import React, { useState } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useLocales } from 'src/locales';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import { useResponsiveConfig } from 'src/components/visibility/utils/use-responsive-config';
import { normalizeLevel } from 'src/components/visibility/utils';
import HoverShortcut from 'src/components/visibility/components/HoverShortcut';
import countriesJson from 'src/utils/countries.json';
import { dashboardPaths } from 'src/routes/dashboardPaths';

type CompetitionEvolutionLevel = 'high' | 'active' | 'low' | string;

type Props = {
  level: CompetitionEvolutionLevel | null;
  brandDomain?: string | null;
  country?: string | null;
  competitorDomain?: string | null;
  brandPresenceRate?: number | null;
  competitorPresenceRate?: number | null;
  isDemoBrand?: boolean;
  fullWidth?: boolean;
};


export default function CompetitionEvolutionCell({ level, brandDomain, country }: Props) {
  const { t } = useLocales();
  const theme = useTheme();
  const navigate = useNavigate();
  const responsiveConfig = useResponsiveConfig();
  const normalizedLevel = React.useMemo(() => {
    const raw = level == null ? '' : String(level).trim();
    const n = normalizeLevel(raw || 'high');
    return n && ['high', 'active', 'low'].includes(n) ? n : 'high';
  }, [level]);

  // Chip style: icon + bg + icon color (Savings opportunity, Protected, Attacked)
  const chipStyle = React.useMemo(() => {
    if (normalizedLevel === 'high') {
      return {
        icon: 'material-symbols:report-outline',
        bg: theme.palette.error.lighter,
        iconColor: theme.palette.error.main,
      };
    }
    if (normalizedLevel === 'active') {
      return {
        icon: 'material-symbols:shield-outline',
        bg: '#24A56C14',
        iconColor: '#00A25B',
      };
    }
    return {
      icon: 'ic:baseline-trending-up',
      bg: 'rgba(183, 220, 251, 0.20)',
      iconColor: 'rgba(0, 108, 156, 0.85)',
    };
  }, [normalizedLevel, theme.palette.error.lighter, theme.palette.error.main]);

  const coverageLabel = React.useMemo(() => {
    switch (normalizedLevel) {
      case 'high':
        return t('dashboard.visibility.competitionEvolutionLevels.high');
      case 'active':
        return t('dashboard.visibility.competitionEvolutionLevels.active');
      case 'low':
        return t('dashboard.visibility.competitionEvolutionLevels.low');
      default:
        return t('dashboard.visibility.competitionEvolutionLevels.high');
    }
  }, [normalizedLevel, t]);

  // Link to Ads page filtered by region (country) and domain
  const handleBrandShortcut = React.useCallback((e: React.MouseEvent) => {
    if (!brandDomain) return;
    const countryEntry = countriesJson?.find((c: any) => c.name === country);
    const code = countryEntry?.code?.toUpperCase() || '';
    const url = `${dashboardPaths.competitiveAds}?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(brandDomain)}`;
    navigate(url);
  }, [brandDomain, country]);

  const seeAdsLabel = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {t('dashboard.global.visibility.seeYourAds')}
      <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
    </Box>
  );
  const [hovered, setHovered] = useState(false);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ position: 'relative', width: '100%', height: '100%', minWidth: 0 }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.5,
          borderRadius: '16px',
          backgroundColor: chipStyle.bg,
          width: 'fit-content',
          flexShrink: 0,
        }}
      >
        <Iconify
          icon={chipStyle.icon}
          width={18}
          height={18}
          sx={{ color: chipStyle.iconColor, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: responsiveConfig.fontSize.body,
            fontWeight: 500,
            color: 'text.primary',
            whiteSpace: 'nowrap',
          }}
        >
          {coverageLabel}
        </Typography>
      </Box>
      {brandDomain && hovered && (
        <Box
          sx={{
            position: 'absolute',
            right: -10,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            pl: 5,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 30%)',
              backdropFilter: 'blur(4px)',
              maskImage: 'linear-gradient(to right, transparent 0%, black 35%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 35%)',
              zIndex: 0,
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <HoverShortcut
              defaultContent=""
              hoverContent={seeAdsLabel}
              onClick={handleBrandShortcut}
              forceHovered
              fontSize={responsiveConfig.fontSize.body}
              sx={{ ml: 0 }}
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
}


